import { v4 as uuid } from 'uuid'
import { memo, MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectActiveLayout, selectActiveLayoutId, selectPrimarySecurity } from "../../selectors/home";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { selectMultiPaneFullScreen } from "../../selectors/common";
import { selectUserDashboards } from "../../selectors/layout";
import { getSavedLayout, isLayoutChanged, processAndUpdateUserDashboards } from "../../util/widgetDashboard";
import { selectAllLayouts } from "../../selectors/widgetDashboard";
import { useChartConfigs } from "../../hooks/useChartConfigs";
import { selectActiveChartId, selectChartConfigs, selectDrawings, selectTimeBuckets } from "../../selectors/chart";
import { areDashboardsLoaded, selectAutoSave, selectUserPreferences } from "../../selectors/user";
import { ChartDrawingItem } from "../chartV2/drawingTools/drawing.interface";
import { saveUserDashboard, toggleSharing } from "../../services/apis/dashboard";
import CustomPrompt from "../../hooks/prompt";
import ROUTES from "../../assets/routes";
import { Divider, ListSubheader, Menu, MenuItem, MenuList, Stack, Tooltip, useTheme, Button } from "@mui/material";
import { ExtraSmallButton, NonInteractiveTooltip } from "../custom";
import { MULTIPANE_FULLSCREEN_MODE_ID } from "../../assets/constants";
import LayoutMenuItem from "./layoutMenuItem";
import PlusIcon from "../../assets/icons/PlusIcon";
import AnalyticsManager from "../../analytics/AnalyticsManager";
import ToggleSwitch from "../common/toggleSwitch/toggleSwitch";
import { updateUserPreferences } from "../../services/apis/user";
import { Group, setFeatureRestrictionDialog, updateUserDashboard } from "../../reducer/user.reducer";
import { SaveMenuShortcutsKeyName } from "../KeyboardShortcuts/ShortcutsKeymap";
import { ShortcutsCategories, ShortcutType } from "../KeyboardShortcuts/Shortcuts.types";
import useShortcuts from "../../hooks/useShortcuts";
import NameDialog from "../common/nameDialog";
import { getDefaultLayout, getDefaultWidget } from '../widgetDashboard/widgetDashboard.helper';
import { WidgetTypes } from '../widgetDashboard/widgetDashboard.enums';
import { getSecurityKey } from '../../util/security';
import { getDefaultChartConfig, getDefaultChartPanesConfig } from '../../util/chart';
import useSecurityExpressionToName from '../../hooks/useExpressionNames';
import { updatePanesConfig, updateSavedChartConfigs } from '../../reducer/chart.reducer';
import { generatePath, matchPath, useNavigate } from 'react-router-dom';
import { updateUserSavedLayouts } from '../../reducer/widgetDashboard.reducer';
import ThinArrowUpIcon from "../../assets/icons/ThinArrowUpIcon";
import ThinArrowDownIcon from "../../assets/icons/ThinArrowDownIcon";
import { blue, darkBg, white } from "../../assets/colors";
import { setActiveLayout } from '../../reducer/home.reducer'
import { copyLayoutData } from './copyLayout'
import { DashboardNamespace, DashboardShareStatus, dashboardShareStatusFromJSON, Platform, UserDashboardProto } from '../../proto/generated/user-preference'
import commonActions from '../../actions/common.actions'
import { selectIsMobile } from '../../selectors/device'
import { FeatureCategory, FeatureName, FeatureSubCategory } from '../adminUserRestrictions/Restrictions.constants';
import { useUser } from '../../context/UserContext';
import Export from '../../assets/icons/Export';
import ExportChartDataDialog from '../exportChartData/ExportChartDataDialog';
import { FeatureCode } from '../../util/RestrictionFeatureCode';

export type LayoutMenuProps = {
    menuId: string,
    disabled?: boolean
}

const LayoutMenu: React.FC<LayoutMenuProps> = ({ menuId, disabled }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const timeBuckets = useSelector(selectTimeBuckets)
    const activeLayout = useSelector(selectActiveLayout);
    const activeChartId = useSelector(selectActiveChartId)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const dashboards = useSelector(selectUserDashboards);
    const dashboardsLoaded = useSelector(areDashboardsLoaded)
    const layouts = useSelector(selectAllLayouts);
    const configs = useSelector(selectChartConfigs)
    const chartConfigs = useChartConfigs(activeLayoutId, activeLayout, configs)
    const drawings: Record<string, ChartDrawingItem[]> = useSelector(selectDrawings);
    const autoSave = useSelector(selectAutoSave);
    const userPreferences = useSelector(selectUserPreferences);
    const { expressionMode, key, expressionSecurities } = useSelector(selectPrimarySecurity)
    const [menuOpen, setMenuOpen] = useState(false);
    const menuAnchor = useRef<HTMLButtonElement>(null);
    const container = useSelector(selectMultiPaneFullScreen);
    const [showCreateNewDialog, setShowCreateNewDialog] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false)
    const expressionToName = useSecurityExpressionToName()
    const [openExportDialog, setOpenExportDialog] = useState(false);
    const isMobile = useSelector(selectIsMobile)
    const {user :{ hasAccess }} = useUser()
    const platform = isMobile?Platform.MOBILE:Platform.DESKTOP

    const handleOpen = useCallback(() => {
        if(!hasAccess(FeatureCategory.CHART, '', FeatureName.DATA_EXPORT)){
            dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: "Chart data export",
                group: Group.CHART,
                featureRestrictionCode:FeatureCode.DATA_EXPORT
            }))
            return
        }
        setOpenExportDialog(true);
    },[hasAccess]);

    const handleClose = () => {
        setOpenExportDialog(false);
    };
    const activeDashboard = useMemo(() => {
        if (!activeLayoutId || !dashboards) return null;
        return dashboards.find(d => d.id === activeLayoutId);
    }, [activeLayoutId, dashboards]);
    const [renameDashboard, setRenameDashboard] = useState<string>()
    const statusEnum = dashboardShareStatusFromJSON(activeDashboard?.shareState);
    const shareLayout =
        statusEnum === DashboardShareStatus.OWNER ||
        statusEnum === DashboardShareStatus.VIEWER;
    const isViewer = dashboardShareStatusFromJSON(activeDashboard?.shareState) === DashboardShareStatus.VIEWER;
    const unsavedLayouts = useMemo(() => {
        return Object.keys(layouts).filter(l => !dashboards.find(d => d.id === l))
    }, [layouts, dashboards])

    const isDashboardChanged = useMemo(() => {
        if (!activeLayout) return false
        if (!activeDashboard?.name) return true
        const savedDashboard = dashboards.find((d) => d.id === activeLayoutId)
        if (!savedDashboard) {
            return !!unsavedLayouts.find(id => id === activeLayoutId)
        }
        return isLayoutChanged(activeLayoutId, activeLayout, savedDashboard, chartConfigs, drawings)
    }, [activeLayout, dashboards, activeLayoutId, chartConfigs, drawings, unsavedLayouts]);

    const handleOpenCloseClick = useCallback(() => {
        setMenuOpen(!menuOpen)
    }, [menuOpen])

    const beforeReload = useCallback(() => {
        window.sessionStorage.setItem('layoutId', activeLayoutId)
    }, [activeLayoutId])

    const onProceed = useCallback(() => {
        processAndUpdateUserDashboards(dashboards).then(({ charts, drawings, layouts }) => {
            dispatch(updateSavedChartConfigs({
                charts,
                drawings
            }))
            dispatch(updateUserSavedLayouts({ replace: true, layouts }))
        })
    }, [dashboards, activeLayout, activeLayoutId])
    const saveLayout = useCallback(async (name?: string, notify = true, autoSave = false) => {
        if (!isDashboardChanged) return;
        const layoutToBeSaved = getSavedLayout(activeLayoutId, layouts[activeLayoutId], chartConfigs, drawings)
        const result = await saveUserDashboard({
            dashboard: {
                id: activeLayoutId,
                layout: JSON.stringify(layoutToBeSaved),
                name: name || activeDashboard?.name || '',
                isDefault: activeDashboard?.isDefault || false,
                isPredefined: false,
                shareState: DashboardShareStatus.NOT_SHARED,
                platform: isMobile ? Platform.MOBILE : Platform.DESKTOP,
                namespace: DashboardNamespace.CHART
            }
        })
        AnalyticsManager.logEvent('Layout Saved', { source: autoSave ? 'autosave' : 'stockbar', name: result.name })
        dispatch(updateUserDashboard(result));
        return result;
    }, [activeLayoutId, layouts, chartConfigs, drawings, isDashboardChanged])


    const autoSaveCallback = useCallback(() => {
        if (!activeDashboard || !activeDashboard.name) return;
        saveLayout('', false, true)
    }, [saveLayout, activeDashboard])

    useEffect(() => {
        if (!isViewer) {
            let intervalId = 0;
            if (autoSave) {
                intervalId = window.setInterval(autoSaveCallback, 1000)
            }

            return () => {
                clearInterval(intervalId)
            }
        }
    }, [autoSave, autoSaveCallback, isDashboardChanged, activeLayoutId, isViewer])

    const saveSharedLayout = useCallback(
        async (dashboard: UserDashboardProto) => {
            if (!dashboard || !chartConfigs || !drawings) return null;

            const { newLayoutId, copiedLayout, newCharts, newDrawings } = copyLayoutData({
                originalLayout: activeLayout,
                chartConfigs,
                drawings,
            });

            const result = await saveUserDashboard({
                dashboard: {
                    id: newLayoutId,
                    layout: JSON.stringify(copiedLayout),
                    name: dashboard.name,
                    isDefault: false,
                    isPredefined: false,
                    shareState: DashboardShareStatus.NOT_SHARED,
                    platform: isMobile ? Platform.MOBILE : Platform.DESKTOP,
                    namespace: DashboardNamespace.CHART
                },
            });

            dispatch(updateUserDashboard(result));
            dispatch(updateSavedChartConfigs({ charts: newCharts, drawings: newDrawings }));
            dispatch(updateUserSavedLayouts({ replace: false, layouts: { [newLayoutId]: copiedLayout } }));
            dispatch(setActiveLayout(newLayoutId));

            return newLayoutId;
        },
        [chartConfigs, drawings, dispatch]
    );

    const onSaveClick = useCallback(async () => {
        if (isViewer) {
            if (!activeDashboard) return;
            const newLayoutId = await saveSharedLayout(activeDashboard);
            if (newLayoutId) {
                navigate(generatePath(ROUTES.chart, { layoutId: newLayoutId }), { replace: true });
            }
            setShowSaveDialog(true);
        } else {
            if (!activeDashboard || !activeDashboard.name) {
                setShowSaveDialog(true);
                return;
            }
            saveLayout();
        }
    }, [saveLayout, saveSharedLayout, activeDashboard, isViewer, navigate]);

    const onSaveShortcut = useCallback((event: any) => {
        AnalyticsManager.logEvent('Save Layout', { source: 'keyboard' })
        event.preventDefault()
        saveLayout()
    }, [saveLayout])

    const shortcuts: ShortcutType = useMemo(() => ({
        // [SaveMenuShortcutsKeyName.openSaveMenu]: handleButtonClick,
        [SaveMenuShortcutsKeyName.saveLayout]: onSaveShortcut
    }), [onSaveShortcut])

    useShortcuts(ShortcutsCategories.SAVE_MENU, shortcuts)

    const handleAutosaveToggle: MouseEventHandler = useCallback(async (e) => {
        updateUserPreferences({
            body: {
                chart: {
                    ...userPreferences.chart,
                    autoSave: !autoSave
                }
            }
        });
    }, [userPreferences, autoSave])


    const handleShareToggle: MouseEventHandler = useCallback(async (e) => {
        if (!activeDashboard) {
            console.error('No active dashboard available');
            return;
        }
        try {
            const response = await toggleSharing({ dashboard: activeDashboard });
            dispatch(updateUserDashboard(response));
        } catch (error) {
            console.error('Error toggling share status:', error);
        }
    }, [activeDashboard, dispatch]);



    const handleCopyLink = useCallback(() => {
        try {
            navigator.clipboard.writeText(window.location.href);
            commonActions.setApiError(dispatch, { error: true, message: 'Copied', errorType: 'success' })
        } catch (error) {
            console.error('Failed to copy link:', error);
        }
    }, []);


    const onNewLayoutClick = useCallback(() => {
        AnalyticsManager.logEvent('Create New layout Clicked ', { source: 'save-menu' })
        if (!isDashboardChanged || (isDashboardChanged && window.confirm('You have unsaved changes, are you sure you want to leave?'))) {
            setShowCreateNewDialog(true);
            setMenuOpen(false);
        }
    }, [isDashboardChanged]);

    const onUnsavedLayoutSave = useCallback((updatedName: string) => {
        saveLayout(updatedName).then((dashboard) => {
            if (dashboard) {
                navigate(generatePath(ROUTES.chart, { layoutId: dashboard.id }), { replace: true })
            }
        });
    }, [saveLayout])

    const onNewLayoutConfirm = useCallback(async (name: string, openInNewTab: boolean) => {
        const newLayoutId = uuid().replace(/[-]*/g, '').split('').slice(0, 10).join('')
        const dashboard = getDefaultLayout()

        const timeBucket = chartConfigs[activeChartId].timeBucket ?? timeBuckets.find(t => t.code === "ONE_MINUTE")
        const securityList = expressionMode ? expressionSecurities.map(v => getSecurityKey(v.exchange, v.code)) : [key]
        const widget = getDefaultWidget(newLayoutId, 1, WidgetTypes.CHART, key, expressionMode, securityList, timeBucket)
        dashboard.widgets[widget.slotId] = widget;
        const chartViewKey = widget.settings.chartId
        const chartConfig = getDefaultChartConfig({
            timeBucket
        })
        const {
            primaryPane,
            seriesGroups,
            seriesObject
        } = await getDefaultChartPanesConfig(chartConfig.chartType, expressionMode, expressionMode ? key : '', securityList, [], undefined, expressionMode ? expressionToName.get(key) || '' : key)

        chartConfig.panes = [primaryPane.id]
        chartConfig.panesConfig = {
            [primaryPane.id]: primaryPane
        }
        chartConfig.seriesGroups = seriesGroups;
        chartConfig.series = seriesObject;

        dispatch(updateSavedChartConfigs({
            charts: {
                [chartViewKey]: chartConfig
            },
            drawings: {
                [chartViewKey]: []
            }
        }))
        dispatch(updatePanesConfig({
            viewKey: chartViewKey,
            panesConfig: [primaryPane],
            series: Object.values(seriesObject),
            seriesGroups: Object.values(seriesGroups)
        }))

        const layoutToBeSaved = {
            panes: dashboard.panes,
            widgets: {
                [widget.slotId]: widget
            },
            syncSettings: dashboard.syncSettings,
            charts: {
                [chartViewKey]: chartConfig
            },
            drawings: {
                [chartViewKey]: []
            }
        }
        const result = await saveUserDashboard({
            dashboard: {
                id: newLayoutId,
                layout: JSON.stringify(layoutToBeSaved),
                name,
                isDefault: false,
                isPredefined: false,
                shareState: DashboardShareStatus.NOT_SHARED,
                platform: isMobile ? Platform.MOBILE : Platform.DESKTOP,
                namespace: DashboardNamespace.CHART
            }
        })
        dispatch(updateUserSavedLayouts({
            replace: false,
            layouts: {
                [newLayoutId]: dashboard
            }
        }))
        AnalyticsManager.logEvent('Layout Created', { source: 'stockbar', name })
        setShowCreateNewDialog(false);
        setMenuOpen(false)
        dispatch(updateUserDashboard(result));
        const url = generatePath(ROUTES.chart, { layoutId: result.id });
        if (openInNewTab) {
            window.open(url, "_blank")
        } else {
            navigate(url)
        }
    }, [layouts, expressionMode, expressionSecurities, key, expressionToName, chartConfigs, activeChartId])

    const onRenameLayoutClick = useCallback((id: string) => {
        AnalyticsManager.logEvent('Rename layout Clicked ', { source: 'save-menu' })

        setRenameDashboard(id)
        setMenuOpen(false);
    }, [dashboards]);

    const onRenameConfirm = useCallback(async (updatedName: string) => {
        const dashboard = dashboards.find(d => d.id === renameDashboard)
        if (!dashboard || updatedName === dashboard.name) return;
        const result = await saveUserDashboard({
            dashboard: {
                ...dashboard,
                name: updatedName,
            }
        })
        AnalyticsManager.logEvent('Layout Saved', { source: 'stockbar', name: updatedName })
        dispatch(updateUserDashboard(result));
        setRenameDashboard("");
    }, [dashboards, renameDashboard]);

    const onRenameCancel = useCallback(() => {
        setRenameDashboard("")
    }, [])

    const openCloseHandle = useMemo(() => <Tooltip title={
        <span style={{ fontSize: "12px" }}>
            Manage layouts : <Box
                sx={{
                    display: 'inline-block',
                    padding: '2px 6px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#fff',
                    backgroundColor: 'grey',
                    borderRadius: '4px',
                    margin: '0 2px',
                    fontFamily: 'monospace',
                }}
            >
                Ctrl
            </Box>
            +
            <Box
                sx={{
                    display: 'inline-block',
                    padding: '2px 6px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#fff',
                    backgroundColor: 'grey',
                    borderRadius: '4px',
                    margin: '0 2px',
                    fontFamily: 'monospace',
                }}
            >P</Box>
        </span>
    } arrow placement='bottom'>
        <ExtraSmallButton
            id={'save-menu-open-button'}
            size="small"
            ref={menuAnchor}
            color="inherit"
            onClick={handleOpenCloseClick}
            data-event={'save-menu Button Clicked'}
            data-event-source={'stockbar'}
            sx={{ padding: "4px 0px", minWidth: "24px" }}
        >
            {menuOpen ? <ThinArrowUpIcon fontSize={"small"} /> : <ThinArrowDownIcon fontSize={"small"} />}
        </ExtraSmallButton>
    </Tooltip>, [menuOpen]);

    const showPrompt = useMemo(() => {

        const isUrlValid = [ROUTES.symbol, ROUTES.chart].reduce((show, route) => {
            return show || !!matchPath(route, window.location.pathname)
        }, false)
        return isDashboardChanged && (isUrlValid && !!dashboards.find(d => d.id === activeLayoutId) || !!unsavedLayouts.find(id => id === activeLayoutId))
    }, [isDashboardChanged, dashboards, activeLayoutId, unsavedLayouts, window.location.pathname])
    return (
        <>
            <CustomPrompt
                message="You have unsaved changes, are you sure you want to leave?"
                when={showPrompt}
                beforeReload={beforeReload}
                // showPrompt={showUnsavedPrompt}
                onProceed={onProceed}
            // resetPrompt={handleResetPrompt}
            />
            <Stack direction="row" alignItems="center" sx={{ opacity: disabled ? 0.5 : 1 }}>

                <Stack direction="column"
                    height={"32px"}
                    alignItems="center"
                    justifyContent={"center"}
                    sx={{ cursor: (isDashboardChanged && !disabled) ? "pointer" : "default" }} // Cursor changes only when clickable
                    onClick={(isDashboardChanged && !disabled) ? onSaveClick : undefined}>
                    <Typography
                        variant="body2"
                        sx={{
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {dashboardsLoaded ? (activeDashboard?.name ?? "Unnamed") : ''}
                    </Typography>

                    {isDashboardChanged && (
                        <Typography
                            component="span"
                            sx={{
                                color: blue,
                                fontSize: "0.75rem",
                                mt: "-2px"
                            }}
                        >
                            Save
                        </Typography>
                    )}
                </Stack>
                {openCloseHandle}
            </Stack>
            {menuOpen && <Menu open={menuOpen} anchorEl={menuAnchor.current}
                container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }} onClose={handleOpenCloseClick}
                slotProps={{
                    paper: {
                        id: menuId
                    }
                }}
            >

                <MenuItem
                    dense
                    key="default"
                    selected={!activeLayoutId}
                    onClick={onNewLayoutClick}
                    data-event={'Create New Layout'}
                    data-event-source={'save-menu'}>
                    <PlusIcon fontSize="small" sx={{ margin: '0 4px 0 -4px' }} /> Create new layout...
                </MenuItem>
                <div><Divider /></div>
                <Stack direction="column" spacing={1}>
                    <Stack direction="row">
                        <MenuItem
                            key="shareLayout"
                            sx={{ width: '100%' }}
                            data-event="Toggle share layout"
                            data-event-source="share-menu"
                        >
                            <NonInteractiveTooltip
                                title={
                                    isViewer
                                        ? "Save your own copy before sharing."
                                        : "Anyone with the link can view your layout"
                                }
                                placement="left"
                            >
                                <Stack direction="column" width="100%">
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        width="100%"
                                    >
                                        <Typography
                                            variant="body1"
                                            fontSize={'14px'}
                                            sx={{ opacity: 0.9 }}
                                        >
                                            Share Layout
                                        </Typography>

                                        <ToggleSwitch
                                            size="small"
                                            disableRipple
                                            data-event-source="multipane"
                                            data-event-toggle="shareLayout toggle"
                                            data-event="shareLayout clicked"
                                            onClick={isViewer ? undefined : handleShareToggle}
                                            checked={shareLayout}
                                            disabled={isViewer}
                                        />
                                    </Stack>

                                    {shareLayout && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                cursor: 'pointer',
                                                color: 'slateblue',
                                                ml: '2px',
                                                mt: '4px'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCopyLink();
                                            }}
                                        >
                                            Copy Link
                                        </Typography>
                                    )}
                                </Stack>
                            </NonInteractiveTooltip>
                        </MenuItem>
                    </Stack>
                </Stack>

                <Stack direction="row">
                    <MenuItem
                        key="autoSave"
                        sx={{ width: '100%' }}
                        data-event="Toggle autoSave"
                        data-event-source="save-menu"
                    >
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                        >
                            <Typography
                                variant="body1"
                                fontSize={'14px'}
                                sx={{ opacity: 0.9 }}
                            >
                                Auto Save
                            </Typography>
                            <ToggleSwitch
                                size="small"
                                disableRipple
                                data-event-source="multipane"
                                data-event-toggle="autoSave toggle"
                                data-event="autoSave clicked"
                                onClick={isViewer ? undefined : handleAutosaveToggle}
                                checked={autoSave}
                                disabled={isViewer}
                            />
                        </Stack>
                    </MenuItem>
                </Stack>
                <MenuItem
                    dense
                    onClick={handleOpen}
                >
                    <Export style={{ fontSize: "20px" }} sx={{ margin: '0 4px 0 -4px' }} /> Download chart data...
                </MenuItem>
                <ExportChartDataDialog
                    open={openExportDialog}
                    onClose={handleClose}
                />
                <div><Divider /></div>
                <ListSubheader
                    sx={{
                        lineHeight: '24px',
                        backgroundColor: 'unset',
                        fontSize: 11,
                        opacity: 0.5,
                        borderTop: 1,
                        borderColor: theme.palette.mode === 'dark' ? darkBg : white
                    }}
                    key="SAVED"
                >
                    SAVED
                </ListSubheader>
                <MenuList disablePadding sx={{ maxHeight: '300px', overflow: 'auto' }}>
                    {dashboards.filter(d => (
                        d.shareState !== DashboardShareStatus.VIEWER
                        && d.platform === platform
                        && d.namespace === DashboardNamespace.CHART
                    )).map(d => <LayoutMenuItem key={d.id} dashboard={d} onRenameClick={onRenameLayoutClick} active={d.id === activeLayoutId}
                        closeMenu={handleOpenCloseClick} />)}
                    {/* {predefinedDashboards?.length ? <>
                                <Divider sx={{ margin: 0 }} />
                                <Typography variant={'caption'} color="textSecondary" sx={{ p: '0 16px' }}>PREDEFINED LAYOUTS
                                        </Typography>
                                {predefinedDashboards.map(d => <LayoutMenuItem dashboard={d} active={false} closeMenu={handleOpenCloseClick} />)}
                                </>
                            : null} */}
                </MenuList>

            </Menu>
            }
            {showCreateNewDialog && <NameDialog
                open
                onClose={() => {
                    setMenuOpen(false);
                    setShowCreateNewDialog(false)
                }}
                onSubmit={onNewLayoutConfirm}
                title="Create Layout"
                subTitle="Name"
                currentName={activeDashboard?.name ?? ''}
                showCheckbox
                defaultOpenInNewTab={false}
            />}
            {/*  For saving symbol page layout */}
            {showSaveDialog && <NameDialog
                open
                onClose={() => {
                    setMenuOpen(false);
                    setShowSaveDialog(false)
                }}
                onSubmit={onUnsavedLayoutSave}
                title="Save Layout"
                subTitle="Name"
                currentName={activeDashboard?.name ?? ''}
                showCheckbox={false}
                defaultOpenInNewTab={false}
            />}

            {renameDashboard && <NameDialog
                open
                onClose={onRenameCancel}
                onSubmit={onRenameConfirm}
                title={`Rename ${dashboards.find(d => d.id === renameDashboard)?.name}`}
                currentName={dashboards.find(d => d.id === renameDashboard)?.name}
                showCheckbox={false}
                defaultOpenInNewTab={false}
            />}
        </>
    );
}

export default memo(LayoutMenu)